import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CommonTable from "../../components/commonComponent/CommanTable";
import axiosInstance from "../../utils/axiosInstance";
import { toastSuccess, toastError } from "../../utils/alertHelper";

// ==================== CONSTANTS ====================
const IMAGE_BASE_URL = process.env.REACT_APP_Image_URL || "https://api.solarestimate.in/image/";

const CLIENT_ORDER_COLUMNS = [
  { key: "srNo", label: "Sr No." },
  { key: "date", label: "Date" },
  { key: "customerName", label: "Customer Name" },
  { key: "qty", label: "QTY" },
  { key: "rate", label: "Rate" },
  { key: "finalOrderRate", label: "Final Order Rate (₹)" }
];

// ==================== HELPER FUNCTIONS ====================
const extractItemFromResponse = (responseData) => {
  if (!responseData) return null;
  // Support multiple response shapes returned by various APIs
  if (responseData.item) return responseData.item;
  if (responseData.Item) return responseData.Item;
  if (responseData.data?.item) return responseData.data.item;
  if (responseData.data?.Item) return responseData.data.Item;
  if (responseData.data) return responseData.data;
  
  return null;
};

const formatPrice = (price) => {
  if (price == null || price === '') return '—';
  return `₹${Number(price).toLocaleString('en-IN')}`;
};

const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  return imagePath.startsWith('http') ? imagePath : `${IMAGE_BASE_URL}${imagePath}`;
};


// ==================== MAIN COMPONENT ====================
export default function ItemView() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  // ==================== FETCH ITEM DATA ====================
  const fetchItem = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      const response = await axiosInstance.get(`/admin/api/item/${id}`);
      const body = response.data || {};
      const isSuccess = body.Status === 1 || body.status === 1;

      if (isSuccess) {
        const itemData = extractItemFromResponse(body);
        
        if (itemData) {
          setItem(itemData);
          toastSuccess(body.Message || body.message || 'Item fetched successfully');
        } else {
          const errorMessage = 'No item data found in response';
          setError(errorMessage);
          toastError(errorMessage);
        }
      } else {
        const errorMessage = body.Message || body.message || "Failed to fetch item";
        setError(errorMessage);
        toastError(errorMessage);
      }
    } catch (error) {
      console.error("Error fetching item:", error);
      const errorMessage = error.response?.data?.Message || 
                          error.response?.data?.message || 
                          "Failed to fetch item. Please try again.";
      setError(errorMessage);
      toastError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchItem();
  }, [fetchItem]);

  // ==================== PREPARE CLIENT ORDERS DATA ====================
  const clientOrderRows = (item?.variants || []).map((variant, index) => ({
    ...variant,
    srNo: index + 1,
    date: variant.date || variant.orderDate || "N/A",
    customerName: variant.customerName || variant.customer?.name || "N/A",
    qty: variant.qty || variant.quantity || "N/A",
    rate: variant.rate ? formatPrice(variant.rate) : "N/A",
    finalOrderRate: variant.finalOrderRate ? formatPrice(variant.finalOrderRate) : "N/A"
  }));

  const filteredClientOrders = clientOrderRows.filter(row =>
    Object.values(row)
      .join(" ")
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  // ==================== RENDER STATES ====================
  if (loading) {
    return (
      <div className="container py-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading item details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger d-flex align-items-center" role="alert">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <div>{error}</div>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => navigate('/admin/item')}
        >
          <i className="fas fa-arrow-left me-2"></i>Back to Items
        </button>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="container py-4">
        <div className="text-center py-5">
          <i className="fas fa-inbox fa-4x text-muted mb-3"></i>
          <h5 className="text-muted">Item not found</h5>
          <button 
            className="btn btn-primary mt-3"
            onClick={() => navigate('/admin/item')}
          >
            <i className="fas fa-arrow-left me-2"></i>Back to Items
        </button>
        </div>
      </div>
    );
  }

  // ==================== MAIN RENDER ====================
  return (
    <div className="container py-4" style={{ maxWidth: 1200 }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="fw-bold mb-0" style={{ fontSize: "1.75rem" }}>View Item</h1>
        <a 
          href="/admin/item" 
          className="text-decoration-none text-secondary d-flex align-items-center"
        >
          <i className="fas fa-arrow-left me-2"></i>Item Master
        </a>
      </div>

      {/* Basic Information Section */}
      <div className="card border-2 shadow-sm mb-4">
        <div className="card-body p-4" style={{backgroundColor: '#fafafa'}}>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5 className="fw-semibold mb-0">Basic Information</h5>
            <a 
              href={`/admin/item/edit/${item._id}`} 
              className="btn btn-sm btn-primary"
            >
              <i className="fas fa-edit me-1"></i>Edit Item
            </a>
          </div>

          <div className="row">
            {/* Image Column */}
            <div className="col-md-3 mb-3">
              <div className="d-flex flex-column align-items-center justify-content-center border rounded p-3" style={{ minHeight: 200, backgroundColor: '#f8f9fa' }}>
                {item.isshowImage && item.image ? (
                  <img
                    src={getImageUrl(item.image)}
                    alt={item.name || 'Item'}
                    className="img-fluid rounded"
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: 180, 
                      objectFit: 'contain' 
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className="text-center text-muted flex-column align-items-center justify-content-center" 
                  style={{ display: (item.isshowImage && item.image) ? 'none' : 'flex' }}
                >
                  <i className="fas fa-image fa-3x mb-2"></i>
                  <small>No Image Available</small>
                </div>
              </div>
            </div>

            {/* Details Columns */}
            <div className="col-md-9">
              <div className="row">
                {/* Column 1 - Name & Basic Info */}
                <div className="col-md-3 mb-3">
                  <div className="mb-3">
                    <small className="text-muted d-block mb-1">Name</small>
                    <div className="fw-semibold">{item.name || '—'}</div>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted d-block mb-1">Item Code</small>
                      <div>{item.code || item.itemCode || item.skucode || 'SE-0001'}</div>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted d-block mb-1">SKU Code</small>
                      <div>{item.sku || item.skuCode || item.skucode || '—'}</div>
                  </div>
                </div>

                {/* Column 2 - Unit & Group */}
                <div className="col-md-3 mb-3">
                  <div className="mb-3">
                    <small className="text-muted d-block mb-1">Unit</small>
                    <div>{(item.unit?.name || item.unit?.unitName || item.unit)}</div>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted d-block mb-1">Group</small>
                    <div>{(item.group?.name || item.group?.groupName || item.group)}</div>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted d-block mb-1">Status</small>
                    <div>
                      {item.isAvailable !== undefined ? (
                        <span className={`badge ${item.isAvailable ? 'bg-success' : 'bg-danger'}`}>
                          {item.isAvailable ? 'Available' : 'Not Available'}
                        </span>
                      ) : '—'}
                    </div>
                  </div>
                </div>

                {/* Column 3 - Tax & Price */}
                <div className="col-md-3 mb-3">
                 <div className="mb-3">
                    <small className="text-muted d-block mb-1">Tax Types</small>
                    <div>{(item.taxType?.name || item.taxType || item.tax?.name || item.tax || 'GST')}</div>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted d-block mb-1">Price(₹)</small>
                    <div className="fw-semibold">
                      {formatPrice(item.salesPrice || item.price)}
                    </div>
                  </div>
                </div>

                {/* Column 4 - Description */}
                <div className="col-md-3 mb-3">
                  <div className="mb-3">
                    <small className="text-muted d-block mb-1">Description</small>
                    <div className="text-break">{item.description || '—'}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Show State Section */}
          {item.stateRate && item.stateRate.length > 0 && (
            <div className="mt-4 pt-3 border-top">
              <small className="text-muted d-block mb-2">Show State</small>
              <div className="d-flex flex-wrap gap-2">
                {item.stateRate.map((sr, index) => {
                  const stateName = sr.state?.name || sr.state?.stateName || sr.state || 'Unknown';
                  const price = sr.price || sr.rate || 0;
                  return (
                    <span 
                      key={sr._id || `state-${index}`} 
                      className="badge bg-success text-white px-3 py-2"
                    >
                      {stateName} - {formatPrice(price)}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Client Orders Section */}
      <div className="card border-2 shadow-sm">
        <div className="card-body p-4" style={{backgroundColor: '#fafafa'}}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="fw-semibold mb-0">
              Client Orders
              <span className="badge bg-primary ms-2">{clientOrderRows.length}</span>
            </h5>
            <input
              type="text"
              className="form-control"
              style={{ maxWidth: 300 }}
              placeholder="Search client orders..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {clientOrderRows.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-shopping-cart fa-4x text-muted mb-3"></i>
              <h6 className="text-muted">No client orders available</h6>
              <p className="text-muted small">This item doesn't have any client orders yet.</p>
            </div>
          ) : (
            <CommonTable
              columns={CLIENT_ORDER_COLUMNS}
              data={filteredClientOrders}
              itemsPerPageOptions={[10, 15, 25, 50]}
              defaultItemsPerPage={15}
              bordered
            />
          )}
        </div>
      </div>
    </div>
  );
}
